// «مجموعتي» (namat-mobile#17, C14): the beneficiary's own group and section,
// the group's members, their rank among them and the group's rank. Pure — the
// query layer (lib/firebase/queries/myGroup.ts) reads, this shapes.
//
// The group and section are the ones the competition ranking already resolves
// (resolveUserContainer in the shared competitionRanking.ts: a subgroup of
// this organization, its section derived from parentGroupId), and the ranks
// are the competition's own (buildContainerMemberRankingPure /
// buildContainerStandingsPure), so this card can never disagree with the
// competition card or the details sheet.
import {
  buildContainerMemberRankingPure,
  buildContainerStandingsPure,
  type SupportGroupRecord,
  type UserContainerResolution,
} from './competitionRanking';

export interface MyGroupMember {
  userId: string;
  /** Rank inside the group in the competition; null when unranked (no competition, or not a beneficiary). */
  rank: number | null;
  completed: number | null;
  isMe: boolean;
}

export interface MyGroupSummary {
  groupId: string;
  groupName: string;
  sectionId: string | null;
  sectionName: string | null;
  /** Ranked members first (best first), then everyone else in the group by id. */
  members: MyGroupMember[];
  /** The viewer's rank among the group's ranked members. */
  myRank: number | null;
  rankedCount: number;
  /** The group's rank among the organization's groups in the competition. */
  groupRank: number | null;
  groupCount: number;
}

export function buildMyGroupSummary(input: {
  currentUserId: string;
  /** The viewer's resolved group/section (resolveUserContainer). */
  myContainer: UserContainerResolution | undefined;
  groupsById: Map<string, SupportGroupRecord>;
  /** userIds with a groupMembers row in the viewer's group (any role). */
  groupMemberIds: string[];
  /** The competition the ranks come from; omitted/null = no ranks. */
  competition?: {
    perUser: Array<{ userId: string; completed: number }>;
    resolutions: Map<string, UserContainerResolution>;
  } | null;
}): MyGroupSummary | null {
  const { currentUserId, myContainer, groupsById, groupMemberIds, competition } = input;
  const groupId = myContainer?.groupId ?? null;
  if (!groupId) return null;
  const sectionId = myContainer?.sectionId ?? null;

  let ranked: MyGroupMember[] = [];
  let groupRank: number | null = null;
  let groupCount = 0;
  if (competition) {
    const groupOf = new Map<string, string | null>(
      Array.from(competition.resolutions.entries()).map(([uid, r]) => [uid, r.groupId])
    );
    ranked = buildContainerMemberRankingPure(currentUserId, groupId, groupOf, competition.perUser).map((r) => ({
      userId: r.userId,
      rank: r.rank,
      completed: r.completed,
      isMe: r.isMe,
    }));
    const groups = Array.from(groupsById.values())
      .filter((g) => g.groupType !== 'parent')
      .map((g) => ({ id: g.id, name: g.name || 'Unknown' }));
    const standings = buildContainerStandingsPure(currentUserId, Infinity, groups, groupOf, competition.perUser);
    groupRank = standings.me?.rank ?? null;
    groupCount = standings.total;
  }

  const seen = new Set(ranked.map((m) => m.userId));
  const others: MyGroupMember[] = Array.from(new Set([...groupMemberIds, currentUserId]))
    .filter((id) => !!id && !seen.has(id))
    .sort()
    .map((userId) => ({ userId, rank: null, completed: null, isMe: userId === currentUserId }));

  return {
    groupId,
    groupName: groupsById.get(groupId)?.name || 'Unknown',
    sectionId,
    sectionName: sectionId ? groupsById.get(sectionId)?.name || 'Unknown' : null,
    members: [...ranked, ...others],
    myRank: ranked.find((m) => m.isMe)?.rank ?? null,
    rankedCount: ranked.length,
    groupRank,
    groupCount,
  };
}
